import { REACTIONS, type Reaction } from '@shared/protocol/messages';
import type { App } from '../../../app/App';
import { ApiError } from '../../../networking/ApiClient';
import { artImg, button } from '../../../ui/components';
import { h, setChildren } from '../../../ui/dom';
import { doodleSvg } from '../../../ui/doodleSvg';
import { t } from '../../../i18n';
import { REACTION_INFO, claimBossPrize, fightResult, fightTarget, noteReaction, syncBoss } from '../../systems/community';
import type { Monster } from '../../systems/model';

/**
 * After a battle against a server-known monster (a friend's shared monster,
 * a Hall of Fame winner, a dare or the weekly boss), tell the server how it
 * went and let the player send the artist a sticker. Returns the panel for
 * the result card, filled in when the server answers (null when signed out
 * or the monster only lives in this notebook).
 */
export function reportCommunityFight(app: App, monster: Monster, outcome: 'me' | 'them' | 'draw', damage: number): HTMLElement | null {
  const target = fightTarget(monster);
  const online = app.store.state.online;
  if (!target || !online) return null;
  app.api.token = online.token;
  const result = fightResult(outcome);
  const artist = monster.author ?? t('a friend');
  const panel = h('div', { class: 'fan-panel' }, h('p', { class: 'hand-hint' }, t('Telling {name} how it went…', { name: artist })));
  const lines: HTMLElement[] = [];

  const stickers = (kind: 'share' | 'hall' | 'dare', ref: string): HTMLElement => {
    const row = h('div', { class: 'reaction-row', role: 'group', 'aria-label': t('Send {name} a sticker', { name: artist }) });
    const send = async (reaction: Reaction): Promise<void> => {
      for (const b of row.querySelectorAll('button')) b.disabled = true;
      try {
        await app.api.react({ kind, ref, reaction });
        app.store.update(['quests', 'memories'], (s) => noteReaction(s, monster, reaction));
        app.sfx.play('pop');
        setChildren(row, h('p', { class: 'reaction-sent' }, t('Sticker sent to {name}!', { name: artist })));
      } catch (err) {
        for (const b of row.querySelectorAll('button')) b.disabled = false;
        app.toast(err instanceof Error ? err.message : t('Could not send the sticker.'), 'error');
      }
    };
    for (const id of REACTIONS) {
      const info = REACTION_INFO[id];
      row.append(
        button(h('span', { class: 'reaction-face' }, artImg(app, `sticker:${info.sticker}`, 'reaction-sticker', doodleSvg(info.sticker, 'reaction-sticker')), h('span', null, t(info.label))), {
          small: true,
          variant: 'ghost',
          title: t(info.label),
          sfx: app,
          onClick: () => void send(id),
        }),
      );
    }
    return h('div', { class: 'reaction-box' }, h('p', { class: 'reaction-ask' }, t('Send {name} a sticker:', { name: artist })), row);
  };

  const run = async (): Promise<void> => {
    let fan: { kind: 'share' | 'hall' | 'dare'; ref: string } | null = null;
    if (target.kind === 'boss') {
      try {
        const res = await app.api.hitBoss({ round: target.round, damage, result });
        let prize = 0;
        app.store.update(['monsters', 'inventory', 'memories'], (s) => {
          syncBoss(s, res);
          prize = claimBossPrize(s, res);
        });
        const b = res.boss;
        if (b) {
          lines.push(h('p', { class: 'boss-hit' }, t('-{n} HP to the boss! Everyone together: {done} / {pool}.', { n: damage, done: b.damage, pool: b.poolHp })));
          if (prize) lines.push(h('p', { class: 'sticky-note green' }, t('The boss is down! Everyone who helped gets +{n} coins.', { n: prize })));
          if (!b.mine) fan = { kind: 'hall', ref: target.entryId };
        }
      } catch (err) {
        lines.push(
          h(
            'p',
            { class: 'hand-hint' },
            err instanceof ApiError && err.code === 'invalid_state'
              ? t('This battle did not count for the boss: you used all your boss battles this week.')
              : err instanceof ApiError && err.code === 'wrong_round'
                ? t('A new boss week has started. Open the contest page to meet the new boss.')
                : t('The battle server did not answer, so this battle did not count for the boss.'),
          ),
        );
      }
    } else {
      try {
        if (target.kind === 'dare') {
          const d = await app.api.answerDare(target.ref, result);
          if (d.status === 'beaten' && outcome === 'me') lines.push(h('p', { class: 'dare-beaten' }, t('Dare beaten! {name} will see it.', { name: artist })));
          fan = { kind: 'dare', ref: target.ref };
        } else if (await app.api.reportFight({ kind: target.kind, ref: target.ref, result })) {
          fan = { kind: target.kind, ref: target.ref };
        }
      } catch {
        lines.push(h('p', { class: 'hand-hint' }, t('The battle server did not answer, so {name} will not hear about this one.', { name: artist })));
      }
    }
    if (fan) lines.push(stickers(fan.kind, fan.ref));
    setChildren(panel, ...lines);
  };
  void run();
  return panel;
}
